import { FC, useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Header } from "@/api"
import AlertMessageSettings, {
   AlertMessageForm,
} from "@/components/AlertMessageSettings"
import { Contact, MessageType } from "@/components/AlertMessageSettings/utils"
import Loader from "@/components/Loader"
import GridItem from "@/components/shared/Grid/GridItem"
import { SpinnerSize } from "@/components/shared/Spinner"
import { AppDispatch, RootState } from "@/store"
import { fetchUsers } from "@/store/usersSlice"
import { AlertChannelForm, AlertEmailMessage } from "@/types/alert"
import { UserStatus } from "@/types/user"

interface MessageProps {
   loading: boolean
   message?: AlertEmailMessage
   onSave: (args: { channels: AlertChannelForm[] }) => void
}

const Message: FC<MessageProps> = ({ loading, message, onSave }) => {
   const dispatch = useDispatch<AppDispatch>()
   const users = useSelector(
      ({ usersReducer }: RootState) => usersReducer.users
   )

   useEffect(() => {
      dispatch(fetchUsers(Header.FACILITY_ID))
   }, [dispatch])

   const contacts = useMemo(
      (): Contact[] =>
         users
            .filter((user) =>
               [UserStatus.ACTIVE, UserStatus.ACTIVE_MFA_BYPASSED].includes(
                  user.status
               )
            )
            .map((user) => ({
               id: user.id,
               name: `${user.firstName} ${user.lastName}`,
               email: user.email,
               phone: `${user.phone.prefix} ${user.phone.number}`,
               role: user.role,
            })),
      [users]
   )

   const form = useMemo(
      (): AlertMessageForm => ({
         activated: message?.emailMessageActivated ?? false,
         recipients: message?.recipients ?? [],
      }),
      [message]
   )

   const handleSave = (state: AlertMessageForm) => {
      onSave({
         channels: [
            {
               id: message?.id,
               active: state.activated,
               recipients: state.recipients,
            },
         ],
      })
   }

   return (
      <GridItem size={3}>
         {loading ? (
            <div className="alert-setting-detail--loading">
               <Loader
                  size={SpinnerSize.SMALL}
                  fullHeight={false}
               />
            </div>
         ) : (
            <AlertMessageSettings
               form={form}
               contacts={contacts}
               messageType={MessageType.EMAIL}
               icon="envelope"
               title="Email notification"
               onSave={handleSave}
            />
         )}
      </GridItem>
   )
}

export default Message
